import { OrganizationType } from '../types/organization.type';
import type { RoleOrgType } from '../auth/role-templates';
import { CANONICAL_ROLE_LABELS, type CanonicalRole } from '../auth/role-templates';

export interface HandbookAction {
  id: string;
  label: string;
  meaning: string;
  note?: string;
}

export interface HandbookEntity {
  id: string;
  label: string;
  summary: string;
  grants: { action: string; description: string }[];
  orgTypes: RoleOrgType[];
}

export interface HandbookRolePreset {
  role: CanonicalRole;
  label: string;
  summary: string;
  typicalPersona: string;
}

export const PERMISSIONS_HANDBOOK_ARTICLE_ID = 'permissions-handbook';

export const HANDBOOK_INTRO =
  'Choose what each employee can see and do in the app. Permissions are grouped by area — for example pickups, branches, or reports. Grant only what their job needs.';

export const HANDBOOK_GRAMMAR = {
  title: 'How access works',
  intro:
    'When you assign collection sites, access follows those branches. All branches means the employee can work anywhere in the organization; specific branches limit them to those locations only.',
  patterns: [
    {
      label: 'All branches',
      description: 'The employee can access every collection site in the organization.',
    },
    {
      label: 'Selected branches',
      description: 'Access is limited to the branches you pick on the user edit page.',
    },
    {
      label: 'Administrator',
      description: 'Full access to manage the organization, team, and settings.',
    },
  ],
  siteScoping:
    'Branch assignment and permissions work together. If you change branches after setting permissions, review access again so nothing is missing.',
};

export const HANDBOOK_ACTIONS: HandbookAction[] = [
  {
    id: 'list',
    label: 'List',
    meaning: 'Browse and search collections (paginated lists).',
    note: 'Does not automatically include viewing a single record (Read).',
  },
  {
    id: 'read',
    label: 'Read',
    meaning: 'View one resource or read-only aggregates (detail pages, dashboards, reports).',
  },
  {
    id: 'create',
    label: 'Create',
    meaning: 'Create new records (typically POST).',
  },
  {
    id: 'write',
    label: 'Write',
    meaning: 'Update existing records (PUT/PATCH) and many file attach/detach operations.',
  },
  {
    id: 'delete',
    label: 'Delete',
    meaning: 'Remove records.',
  },
  {
    id: 'manage',
    label: 'Manage',
    meaning: 'Elevated operations beyond normal read/write (HSE weekly reports, utility-month init, training program admin).',
    note: 'Does not imply Read — grant both if needed.',
  },
  {
    id: 'assign',
    label: 'Assign',
    meaning: 'Link resources to people or vehicles (pickup assignment, driver to truck, user site assignments via role.assign).',
  },
  {
    id: 'accept',
    label: 'Accept',
    meaning: 'Accept a workflow item (catalog action; API may require Manage on assignments).',
  },
  {
    id: 'reject',
    label: 'Reject',
    meaning: 'Reject a workflow item (catalog action; API may require Manage on assignments).',
  },
  {
    id: 'cancel',
    label: 'Cancel',
    meaning: 'Cancel an in-flight workflow (e.g. pickup pre-load cancel).',
  },
  {
    id: 'complete',
    label: 'Complete',
    meaning: 'Mark a batch or workflow complete (e.g. delivery aggregation).',
  },
  {
    id: 'register',
    label: 'Register',
    meaning: 'Onboard accounts (driver registration, platform user register).',
  },
  {
    id: 'submit',
    label: 'Submit',
    meaning: 'Create approval requests and cancel own pending items (approval workflow).',
    note: 'Separate from Write (approve/reject) for segregation of duties.',
  },
];

export const HANDBOOK_ENTITIES: HandbookEntity[] = [
  {
    id: 'organization',
    label: 'Organization',
    summary: 'Organization profile and settings.',
    grants: [
      { action: 'read', description: 'View organization profile' },
      { action: 'write', description: 'Update organization settings' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'user',
    label: 'User',
    summary:
      'Team members in your organization. List access opens the Users page; edit access is required to change employee profiles and permissions.',
    grants: [
      { action: 'list', description: 'View the employee list' },
      { action: 'write', description: 'Edit profiles and permissions' },
      { action: 'delete', description: 'Remove team members from the organization' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'role',
    label: 'Role',
    summary:
      'Role and branch assignment. Assign access is required to choose which collection sites an employee can work at.',
    grants: [
      { action: 'list', description: 'View role definitions (reserved for future use)' },
      { action: 'assign', description: 'Assign, replace, or revoke collection sites for employees' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'document',
    label: 'Document',
    summary: 'Shared organization documents.',
    grants: [
      { action: 'list', description: 'List organization documents' },
      { action: 'write', description: 'Upload or update documents' },
      { action: 'delete', description: 'Remove documents' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'approval',
    label: 'Approvals',
    summary:
      'Workflow inbox for sensitive actions requiring justification or approver sign-off.',
    grants: [
      { action: 'list', description: 'Browse approval queue and history' },
      { action: 'read', description: 'View single request details and preview diff' },
      { action: 'submit', description: 'Submit approval requests; cancel own pending requests' },
      {
        action: 'write',
        description: 'Approve or reject pending requests from other users',
      },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'pickup_order',
    label: 'Pickup order',
    summary: 'Waste collection pickup requests and driver workflows.',
    grants: [
      { action: 'list', description: 'Pickup board and available orders for drivers' },
      { action: 'read', description: 'Order detail and attached files' },
      { action: 'create', description: 'Create pickup orders' },
      { action: 'write', description: 'Update orders and files' },
      { action: 'delete', description: 'Delete orders' },
      { action: 'assign', description: 'Assign orders to drivers' },
      { action: 'cancel', description: 'Cancel in-flight pickups' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.TRUCK],
  },
  {
    id: 'pre_order',
    label: 'Pre-order',
    summary: 'Planned pickups before they are scheduled.',
    grants: [
      { action: 'list', description: 'Browse pre-orders' },
      { action: 'read', description: 'View pre-order detail' },
      { action: 'create', description: 'Create pre-orders' },
      { action: 'write', description: 'Update pre-orders' },
      { action: 'delete', description: 'Delete pre-orders' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY],
  },
  {
    id: 'collection_site',
    label: 'Collection site',
    summary:
      'Branch locations, site settings, HSE, and utility tracking. Viewing a branch alone does not include HSE weekly reports or utility-month setup — grant Manage for those.',
    grants: [
      { action: 'list', description: 'List all sites in the organization' },
      { action: 'create', description: 'Create new collection sites' },
      { action: 'read', description: 'Site statistics and read-only site data' },
      { action: 'write', description: 'Update site settings, utility settings, and proofs' },
      { action: 'delete', description: 'Delete a site' },
      { action: 'manage', description: 'HSE weekly reports and utility-month setup' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY],
  },
  {
    id: 'container',
    label: 'Container',
    summary: 'On-site containers at collection branches.',
    grants: [
      { action: 'list', description: 'List containers (includes practical read access)' },
      { action: 'create', description: 'Create containers' },
      { action: 'write', description: 'Update containers' },
      { action: 'delete', description: 'Delete containers' },
      { action: 'read', description: 'View container details (listing also allows basic viewing)' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY],
  },
  {
    id: 'material_type',
    label: 'Material type',
    summary: 'Waste material categories and classifications.',
    grants: [
      { action: 'list', description: 'Browse material types' },
      { action: 'create', description: 'Create material types' },
      { action: 'write', description: 'Update material types' },
      { action: 'delete', description: 'Delete material types' },
      { action: 'read', description: 'View material type details (listing also allows basic viewing)' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER],
  },
  {
    id: 'delivery',
    label: 'Delivery',
    summary: 'Inbound material deliveries for recyclers.',
    grants: [
      { action: 'list', description: 'Delivery list and addable orders' },
      { action: 'read', description: 'Delivery detail and events' },
      { action: 'create', description: 'Create deliveries' },
      { action: 'write', description: 'Update deliveries, phases, incidents' },
      { action: 'delete', description: 'Delete deliveries' },
      { action: 'complete', description: 'Complete delivery batches' },
    ],
    orgTypes: [OrganizationType.RECYCLER],
  },
  {
    id: 'purchase_order',
    label: 'Purchase order',
    summary: 'Purchase orders with suppliers.',
    grants: [
      { action: 'list', description: 'Browse purchase orders' },
      { action: 'read', description: 'View order detail' },
      { action: 'create', description: 'Create purchase orders' },
      { action: 'write', description: 'Update purchase orders' },
      { action: 'delete', description: 'Delete purchase orders' },
    ],
    orgTypes: [OrganizationType.RECYCLER],
  },
  {
    id: 'offer',
    label: 'Offer',
    summary: 'Material offers from partners.',
    grants: [
      { action: 'list', description: 'Browse offers' },
      { action: 'read', description: 'View offer detail' },
      { action: 'create', description: 'Create offers' },
      { action: 'write', description: 'Update offers' },
      { action: 'delete', description: 'Delete offers' },
    ],
    orgTypes: [OrganizationType.RECYCLER],
  },
  {
    id: 'appetite',
    label: 'Appetite',
    summary: 'Recycler material demand and fulfillment settings.',
    grants: [
      { action: 'read', description: 'View appetite fulfillment' },
      { action: 'write', description: 'Update recycler appetite' },
    ],
    orgTypes: [OrganizationType.RECYCLER],
  },
  {
    id: 'truck',
    label: 'Truck',
    summary: 'Fleet vehicles and live tracking.',
    grants: [
      { action: 'list', description: 'Fleet list and tracking map' },
      { action: 'read', description: 'Single truck tracking detail' },
      { action: 'create', description: 'Add trucks' },
      { action: 'write', description: 'Update trucks and GPS location' },
      { action: 'delete', description: 'Remove trucks' },
      { action: 'assign', description: 'Assign or unassign drivers' },
    ],
    orgTypes: [OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'driver',
    label: 'Driver',
    summary: 'Driver profiles for truck companies.',
    grants: [
      { action: 'register', description: 'Register drivers' },
      { action: 'read', description: 'View driver profile' },
    ],
    orgTypes: [OrganizationType.TRUCK],
  },
  {
    id: 'assignment',
    label: 'Assignment',
    summary:
      'Driver route assignments and pickup responses. Drivers who accept or reject jobs on their phone need Manage access, not only Accept or Reject.',
    grants: [
      { action: 'list', description: 'List driver assignments' },
      { action: 'read', description: 'View assignment and truck linkage' },
      { action: 'manage', description: 'Accept or reject job assignments in the driver app' },
      {
        action: 'accept',
        description: 'Accept jobs (usually paired with Manage for the driver app)',
      },
      {
        action: 'reject',
        description: 'Decline jobs (usually paired with Manage for the driver app)',
      },
    ],
    orgTypes: [OrganizationType.TRUCK],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    summary:
      'Operational dashboards and KPI views. Does not include HSE weekly reports at branches — those are under Collection site Manage access.',
    grants: [{ action: 'read', description: 'Organization overview and monthly dashboard pages' }],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'esg_report',
    label: 'ESG report',
    summary: 'Environmental and carbon reporting.',
    grants: [{ action: 'read', description: 'ESG dashboards and carbon avoidance reports' }],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER],
  },
  {
    id: 'forms_submission',
    label: 'Forms',
    summary: 'Operational form submissions.',
    grants: [
      { action: 'create', description: 'Submit operational forms' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER],
  },
  {
    id: 'invoice',
    label: 'Invoice',
    summary: 'Invoice listing and management.',
    grants: [
      { action: 'list', description: 'List invoices for the organization' },
      { action: 'read', description: 'Open invoice details' },
      { action: 'create', description: 'Create invoices' },
      { action: 'write', description: 'Update invoices' },
      { action: 'delete', description: 'Delete invoices' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.RECYCLER],
  },
  {
    id: 'notification',
    label: 'Notification',
    summary: 'In-app alerts and read status.',
    grants: [
      { action: 'list', description: 'Notification inbox' },
      { action: 'write', description: 'Mark notifications as read' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'training_program',
    label: 'Training program',
    summary:
      'Training curricula and organization-wide training admin. Managing the training summary for the whole org requires Manage — viewing one person’s records is not enough.',
    grants: [{ action: 'manage', description: 'Training summary, create sessions, and training widgets' }],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'training_record',
    label: 'Training record',
    summary: 'Per-employee training completion.',
    grants: [{ action: 'read', description: 'View a user training sessions' }],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
  {
    id: 'report',
    label: 'Report',
    summary: 'Generated operational reports.',
    grants: [
      { action: 'list', description: 'List reports' },
      { action: 'read', description: 'Open report details' },
      { action: 'create', description: 'Generate new reports' },
    ],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER],
  },
  {
    id: 'upload',
    label: 'Upload',
    summary: 'File uploads and presigned URLs.',
    grants: [{ action: 'create', description: 'Generate upload URLs and attach files' }],
    orgTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.CHARITY, OrganizationType.RECYCLER, OrganizationType.TRUCK],
  },
];

export const HANDBOOK_ROLE_PRESETS: HandbookRolePreset[] = [
  {
    role: 'admin',
    label: CANONICAL_ROLE_LABELS.admin,
    summary: 'Full organization access including user, role, and document management.',
    typicalPersona: 'Organization owner or IT administrator',
  },
  {
    role: 'ops_manager',
    label: CANONICAL_ROLE_LABELS.ops_manager,
    summary:
      'Site-scoped operations (pickups, sites, containers) plus dashboards, training admin, reports, and invoices when relevant to the organization type.',
    typicalPersona: 'Branch or operations manager',
  },
  {
    role: 'staff',
    label: CANONICAL_ROLE_LABELS.staff,
    summary: 'Day-to-day operator access: create/edit pickups, read sites and containers, notifications, uploads.',
    typicalPersona: 'Site operator or frontline staff',
  },
  {
    role: 'admin_read_only',
    label: CANONICAL_ROLE_LABELS.admin_read_only,
    summary: 'View-only across operational data, dashboards, and reports without write access.',
    typicalPersona: 'Auditor or executive viewer',
  },
];

export const HANDBOOK_IMPLICATIONS = [
  'Viewing something does not let someone edit or delete it — grant edit access separately when needed.',
  'Seeing a list of items is not the same as opening each item’s details.',
  'Dashboard access does not include HSE weekly reports or utility tracking — those need branch management access.',
  'Viewing training records for one person is not the same as managing training for the whole organization.',
  'Drivers may need assignment management to accept or reject jobs in the app, not only accept/reject toggles.',
  'Administrator role gives broad access; use Staff or Read-only when you want to limit changes.',
];

export interface HandbookUiFeature {
  feature: string;
  description: string;
  area: string;
}

export const HANDBOOK_UI_GATING: HandbookUiFeature[] = [
  {
    area: 'Team',
    feature: 'Users page',
    description: 'Open the employee list and see who works in the organization.',
  },
  {
    area: 'Team',
    feature: 'Edit employee',
    description: 'Change profiles, roles, and access for existing team members.',
  },
  {
    area: 'Team',
    feature: 'Assign branches',
    description: 'Choose which collection sites an employee can work at.',
  },
  {
    area: 'Branches',
    feature: 'Browse branches',
    description: 'See the list of collection sites when assigning access.',
  },
  {
    area: 'Branches',
    feature: 'Edit branch settings',
    description: 'Update site details, utility settings, and proofs for a branch.',
  },
  {
    area: 'Branches',
    feature: 'HSE weekly report',
    description: 'View and manage health, safety, and environment weekly reports.',
  },
  {
    area: 'Training',
    feature: 'Training overview',
    description: 'See organization-wide training summary and add sessions.',
  },
  {
    area: 'Operations',
    feature: 'Pickup orders',
    description: 'View the pickup board and available orders.',
  },
  {
    area: 'Operations',
    feature: 'Create pickups',
    description: 'Schedule new waste collection requests.',
  },
  {
    area: 'Fleet',
    feature: 'Driver job responses',
    description: 'Let drivers accept or decline assignments in the app.',
  },
  {
    area: 'General',
    feature: 'Upload files',
    description: 'Attach documents and images where the app allows uploads.',
  },
];

export function handbookEntitiesForOrgType(orgType: RoleOrgType): HandbookEntity[] {
  return HANDBOOK_ENTITIES.filter((entity) => entity.orgTypes.includes(orgType));
}

export function buildPermissionsHandbookArticleContent(): string {
  const roleLines = HANDBOOK_ROLE_PRESETS.map(
    (r) => `• ${r.label} — ${r.summary} (${r.typicalPersona})`,
  ).join('\n');

  const tipLines = HANDBOOK_IMPLICATIONS.map((t) => `• ${t}`).join('\n');

  const featureLines = HANDBOOK_UI_GATING.map(
    (f) => `• ${f.feature} — ${f.description}`,
  ).join('\n');

  return `${HANDBOOK_INTRO}

How branch access works

${HANDBOOK_GRAMMAR.patterns.map((p) => `• ${p.label} — ${p.description}`).join('\n')}

${HANDBOOK_GRAMMAR.siteScoping}

Role presets

${roleLines}

What each app area needs

${featureLines}

Good to know

${tipLines}

For a full breakdown by area (pickups, branches, reports, and more), open Permission reference from the Role & access card when editing an employee.`;
}
